import { useForm } from 'react-hook-form';
import { OpenBankingFeedBack } from '@graphql/index';
import { Box } from '@spark-web/box';
import { Button } from '@spark-web/button';
import { Checkbox } from '@spark-web/checkbox';
import { Field } from '@spark-web/field';
import { ContentDialog } from '@spark-web/modal-dialog';
import { Stack } from '@spark-web/stack';
import { Text } from '@spark-web/text';
import { INCOME_VERIFICATION, LABELS } from '@utils';

interface OpenBankingFeedbackModalProps {
  isOpen: boolean;
  onSubmit: (feedbacks: Array<OpenBankingFeedBack>) => void;
  onToggle: () => void;
}

type FeedbacksFormValues = {
  feedbacks: Array<OpenBankingFeedBack>;
};

export function OpenBankingFeedbackModal({ isOpen, onSubmit, onToggle }: OpenBankingFeedbackModalProps) {
  const { CONTINUE, CANCEL } = LABELS;
  const {
    FEEDBACK_PROBLEMS_TITLE,
    FEEDBACK_PROBLEMS_FEEDBACK,
    FEEDBACK_PROBLEMS_SUBTITLE,
    FEEDBACK_UNSELECTED,
    FEEDBACK_PROBLEMS_OPTIONS,
    FEEDBACK_PROBLEMS_OPTIONS_LABELS,
  } = INCOME_VERIFICATION;

  const { formState, setValue, setError, watch, reset } = useForm<FeedbacksFormValues>({
    defaultValues: { feedbacks: [] },
  });
  const feedbacks = watch('feedbacks');

  const handleCancel = () => {
    reset();
    onToggle();
  };

  const handleSubmit = () => {
    if (!feedbacks.length) {
      setError('feedbacks', { type: 'manual', message: FEEDBACK_UNSELECTED });
      return;
    }
    reset();
    onSubmit(feedbacks);
  };

  const handleFeedbackChange = (feedback: OpenBankingFeedBack) => {
    const newFeedbacks = feedbacks.includes(feedback)
      ? feedbacks.filter(q => q !== feedback)
      : [...feedbacks, feedback];
    setValue('feedbacks', newFeedbacks);
  };

  return (
    <ContentDialog
      title={FEEDBACK_PROBLEMS_TITLE}
      isOpen={isOpen}
      onToggle={handleCancel}
      footer={
        <Box display="flex" gap="xsmall">
          <Button onClick={handleCancel} prominence="none" tone="neutral">
            {CANCEL}
          </Button>
          <Button onClick={handleSubmit}>{CONTINUE}</Button>
        </Box>
      }
    >
      <Text>{FEEDBACK_PROBLEMS_SUBTITLE}</Text>
      <Box marginTop="medium">
        <Text weight="semibold">{FEEDBACK_PROBLEMS_FEEDBACK}</Text>
      </Box>
      <Field
        label=""
        tone={formState.errors.feedbacks ? 'critical' : undefined}
        message={formState.errors.feedbacks ? FEEDBACK_UNSELECTED : undefined}
      >
        <Stack gap="medium">
          {FEEDBACK_PROBLEMS_OPTIONS.map(feedback => (
            <Box key={feedback} border="neutral" borderRadius="small" padding="large">
              <Checkbox
                tone="critical"
                onClick={() => {
                  handleFeedbackChange(feedback);
                }}
              >
                <Text tone="neutral" weight="semibold">
                  {FEEDBACK_PROBLEMS_OPTIONS_LABELS[feedback]}
                </Text>
              </Checkbox>
            </Box>
          ))}
        </Stack>
      </Field>
    </ContentDialog>
  );
}
